import type { ChangeEvent } from 'react'

/**
 * JEPX 制度上限 (spec spot-bidding.md §7 AC-050/051、 intraday-bidding.md §6 AC-038 共通)。
 * 価格: 0.01〜999 円/kWh (0.01 円/kWh 単位)。
 */
export const JEPX_PRICE_CONSTRAINT = {
  min: 0.01,
  max: 999,
  step: 0.01,
} as const

/**
 * JEPX 数量値域 (spec AC-050/051)。 **MW 建て** — コマ別数量は入力・表示・保持・
 * eSLJ 送信のすべてが MW で、 単位の境界変換は存在しない (issue #4285)。
 *   - min 0.1 MW / max 9999.9 MW / step 0.1 MW (= 制度の最小入札単位そのもの)
 * VO `BidVolumeMw` / DB numeric(10,1) の粒度も 0.1 なので、 制度刻みがそのまま
 * 保存できる (= 旧 MWh 保持時代の 0.2 MW 刻み制約 issue #4217 は解消済み)。
 */
export const JEPX_VOLUME_CONSTRAINT = {
  min: 0.1,
  max: 9999.9,
  step: 0.1,
} as const

/**
 * value が step の整数倍から外れているか (= JEPX の刻み条件)。 float 誤差
 * (0.1 + 0.2 !== 0.3) を避けるため剰余ではなく、 step 単位に丸めた整数との差を
 * tolerance 付きで比較する。
 */
export const violatesJepxStep = (value: number, step: number): boolean => {
  const units = Math.round(value / step)
  return Math.abs(units * step - value) > step / 1000
}

/** backend が小数として受け付ける文字列長の上限。 */
const MAX_DECIMAL_INPUT_LENGTH = 64

/**
 * 指数表記 (`1e2`) を含まない 10 進数の入力か。 `<input type="number">` は指数表記も
 * 受け付け、 `Number()` も有限値として読むが、 backend は小数の指数表記と 64 文字を超える値を
 * 入力エラーとして拒否する。 入力値を文字列のまま RPC に載せる欄は、 送る前にこれで弾く。
 */
export const isPlainDecimalInput = (raw: string): boolean =>
  raw.length <= MAX_DECIMAL_INPUT_LENGTH &&
  /^-?(?:\d+(?:\.\d*)?|\.\d+)$/.test(raw)

type NumberConstraint = { max: number; step: number }

const decimalPlacesOf = (step: number): number => {
  const s = String(step)
  const dot = s.indexOf('.')
  return dot === -1 ? 0 : s.length - dot - 1
}

/**
 * JEPX 価格・数量入力欄の decimal 桁数 / 上限値を超える入力を「入力自体受け付けない」
 * (issue #3304 AC-3304-1)。 typing で末尾に追加された桁が制約を超えたケースを想定し、
 * 超過分を末尾から切り詰める (= 途中挿入等の稀な操作までは完全に再現しないが、
 * 主要な typing 経路〈末尾追記〉を確実にブロックする)。
 *
 * 下限 (min) 未満は "0" → "0.01" のような途中入力の正当な通過点になり得るため、
 * ここでは拒否しない (= 下限違反は既存の submit 時エラーメッセージ〈価格は 0 以上で
 * 入力してください 等〉に委ねる。 spec spot-bidding.md AC-062 の「価格・数量の文言に
 * JEPX 値域を記載しない」 とも整合する)。
 */
export const constrainNumberInput = (
  raw: string,
  constraint: NumberConstraint,
): string => {
  if (raw === '') return raw
  let value = raw
  const decimalsAllowed = decimalPlacesOf(constraint.step)
  const dot = value.indexOf('.')
  if (dot !== -1 && value.length - dot - 1 > decimalsAllowed) {
    value = value.slice(0, dot + 1 + decimalsAllowed)
  }
  while (value !== '' && Number(value) > constraint.max) {
    value = value.slice(0, -1)
  }
  return value
}

/**
 * React Hook Form `register(...)` 返り値の `onChange` を、 上記 `constrainNumberInput` で
 * ラップした onChange ハンドラに差し替える。
 * `e.target.value` を制約後の値に書き換えてから元の `onChange` を呼ぶことで、
 * RHF が読み取る値も制約後の値になる (= uncontrolled input の標準パターン)。
 */
export const wrapNumberInputChange =
  (
    registration: { onChange: (e: ChangeEvent<HTMLInputElement>) => void },
    constraint: NumberConstraint,
  ) =>
  (e: ChangeEvent<HTMLInputElement>): void => {
    const constrained = constrainNumberInput(e.target.value, constraint)
    if (constrained !== e.target.value) {
      e.target.value = constrained
    }
    registration.onChange(e)
  }
